/**
 * PageHero — Identity-strong page-top block (ESPN-flavored).
 *
 * Renders a dark-purple gradient with optional avatar (left) + eyebrow / title /
 * caps metadata strip + optional right-anchored action. A 4px gold rule sits
 * along the bottom edge.
 *
 * Used by HomePage (athlete identity), and a refactor target for any future
 * page that wants the identity-strong treatment.
 */

import { ReactNode } from 'react';

export interface PageHeroProps {
  /** Caps eyebrow text above the title. */
  eyebrow?: string;
  /** Main title. Required. */
  title: string;
  /**
   * Caps metadata strip below the title. Provided as an array of segments;
   * null/undefined/empty entries are dropped, surviving entries joined with " · ".
   * If all entries are empty, no meta strip renders (no fallback).
   */
  meta?: (string | null | undefined)[];
  /** Avatar image source. */
  avatarSrc?: string;
  /** Initials fallback when no avatarSrc; renders inside a circle. */
  avatarInitials?: string;
  /** Right-anchored action element. */
  action?: ReactNode;
  /** Extra className appended to the outer hero. */
  className?: string;
}

/** Compute initials from a display name (1–2 uppercase chars). */
export function getInitialsFromName(name: string | null | undefined): string {
  if (!name) return 'A';
  const initials = name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return initials || 'A';
}

export function PageHero({
  eyebrow,
  title,
  meta,
  avatarSrc,
  avatarInitials,
  action,
  className,
}: PageHeroProps) {
  const showAvatar = avatarSrc != null || avatarInitials != null;
  const filteredMeta = (meta ?? [])
    .filter((v) => v != null && String(v).trim() !== '')
    .map((v) => String(v));
  const metaText = filteredMeta.join(' · ');

  return (
    <header className={`page-hero${className ? ' ' + className : ''}`}>
      {action && <div className="page-hero-action-slot">{action}</div>}
      <div className="page-hero-content">
        {showAvatar && (
          <div className="page-hero-avatar">
            {avatarSrc ? (
              <img src={avatarSrc} alt={title} />
            ) : (
              <span className="page-hero-avatar-initials">{avatarInitials}</span>
            )}
          </div>
        )}
        <div className="page-hero-text">
          {eyebrow && (
            <p
              className="page-section-label"
              style={{ color: 'rgba(255, 255, 255, 0.7)', marginBottom: 6 }}
            >
              {eyebrow}
            </p>
          )}
          <h1 className="page-hero-title">{title}</h1>
          {metaText && <p className="page-hero-meta">{metaText}</p>}
        </div>
      </div>
    </header>
  );
}

export default PageHero;
